/**
 * useTourVerification
 *
 * Watches for training events and DOM changes to automatically verify a step.
 */

'use client';

import { useEffect, useState, useCallback } from 'react';
import type { TourStep, TourVerification } from '@/config/guided-tours/types';

export const GUIDED_TOUR_TRAINING_EVENT = 'guided-tour-training';

/**
 * Dispatched by the tour runner when the user advances past a step that
 * revealed a modal (a step with `reveal_target`). Modal hosts (e.g. Header)
 * listen for this and close any open modal so the next tour step can re-guide
 * the user back to the dropdown.
 */
export const GUIDED_TOUR_DISMISS_REVEAL_EVENT = 'guided-tour-dismiss-reveal';

export interface GuidedTourTrainingEventDetail {
  type: string;
  occurredAt: string;
  stepId?: string | null;
  metadata?: Record<string, unknown> | null;
}

interface UseTourVerificationOptions {
  step: TourStep | null;
  stepId: string | null;
  verifiedStepIds: string[];
  onVerify: (stepId: string) => void;
  enabled?: boolean;
  /**
   * Optional callback that returns true if DOM evidence should be ignored
   * for the current check (e.g. during a step transition where the previous
   * step's target element is still visible). The callback is expected to
   * consume (reset) the suppression flag.
   */
  consumeDomEvidenceSuppression?: () => boolean;
}

function getVerification(step: TourStep | null): TourVerification | null {
  if (!step) {
    return null;
  }

  return step.verification ?? null;
}

export function useTourVerification({
  step,
  stepId,
  verifiedStepIds,
  onVerify,
  enabled = true,
  consumeDomEvidenceSuppression,
}: UseTourVerificationOptions) {
  const verification = getVerification(step);
  const isVerified = stepId ? verifiedStepIds.includes(stepId) : false;
  const [isDomEvidencePresent, setIsDomEvidencePresent] = useState(false);

  const checkDomEvidence = useCallback(() => {
    if (!verification?.target_selector || typeof document === 'undefined') {
      setIsDomEvidencePresent(false);
      return;
    }

    // If the engine just performed a step transition, suppress DOM evidence
    // checks so the previous step's target element (still visible in the DOM)
    // doesn't incorrectly auto-verify the new step.
    if (consumeDomEvidenceSuppression?.()) {
      setIsDomEvidencePresent(false);
      return;
    }

    const selectors = [
      verification.target_selector,
      ...(verification.alternative_selectors ?? []),
    ];

    const present = selectors.some((selector) => {
      if (!selector) {
        return false;
      }

      const element = document.querySelector(selector);
      return !!element && element.checkVisibility();
    });

    setIsDomEvidencePresent(present);
  }, [verification, consumeDomEvidenceSuppression]);

  // Listen for training events that match the verification event_type.
  useEffect(() => {
    if (!enabled || !stepId || !verification?.event_type || isVerified) {
      return;
    }

    const handleEvent = (event: Event) => {
      const detail = (event as CustomEvent<GuidedTourTrainingEventDetail>).detail;
      if (!detail?.type) {
        return;
      }

      if (detail.type === verification.event_type) {
        onVerify(stepId);
      }
    };

    window.addEventListener(GUIDED_TOUR_TRAINING_EVENT, handleEvent as EventListener);
    return () => {
      window.removeEventListener(GUIDED_TOUR_TRAINING_EVENT, handleEvent as EventListener);
    };
  }, [enabled, isVerified, onVerify, stepId, verification]);

  // Observe DOM evidence if the verification defines a target_selector.
  useEffect(() => {
    if (!enabled || !verification?.target_selector || isVerified) {
      return;
    }

    // Defer the initial DOM scan into a microtask to avoid cascading renders.
    queueMicrotask(checkDomEvidence);

    if (typeof MutationObserver === 'undefined' || !document.body) {
      return;
    }

    const observer = new MutationObserver(() => {
      checkDomEvidence();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // NOTE: 'style' is intentionally excluded — it fires on every CSS
      // transition/animation in the document, creating excessive observer
      // callbacks. class/childList changes are sufficient to detect elements
      // appearing, disappearing, or becoming visible.
      attributeFilter: ['class', 'data-tour-id', 'hidden'],
    });

    // Slow poll as a backstop — a step whose DOM is completely static after
    // the transition (e.g. an alternative selector that was already mounted
    // before the step began) would otherwise never re-check once the initial
    // suppressed scan consumed the suppression flag.
    const poll = window.setInterval(checkDomEvidence, 750);

    return () => {
      observer.disconnect();
      window.clearInterval(poll);
    };
  }, [enabled, isVerified, verification, checkDomEvidence]);

  const canAdvance = isVerified || isDomEvidencePresent;

  return {
    verification,
    isVerified,
    isDomEvidencePresent,
    canAdvance,
    checkDomEvidence,
  };
}

export function emitGuidedTourTrainingEvent(
  detail: Omit<GuidedTourTrainingEventDetail, 'occurredAt'> &
    Partial<Pick<GuidedTourTrainingEventDetail, 'occurredAt'>>
): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<GuidedTourTrainingEventDetail>(GUIDED_TOUR_TRAINING_EVENT, {
      detail: {
        ...detail,
        occurredAt: detail.occurredAt || new Date().toISOString(),
      },
    })
  );
}
